import type { NextPage } from 'next';
import { ApolloProvider } from '@apollo/client';
import { createApolloClient, getApolloClient } from './client';

// eslint-disable-next-line @typescript-eslint/ban-types
type NextProps = {};

// Gets the display name of a JSX component for dev tools
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getDisplayName(Component: React.ComponentType<any>) {
    return Component.displayName || Component.name || 'Unknown';
}

/**
 * Always creates a new apollo client on the server so that data
 * isn't shared between connections.
 * Creates or reuses apollo client in the browser.
 */
export default function withApollo<P extends NextProps = NextProps>(
    Page: NextPage<P>
) {
    const isBrowser = typeof window !== 'undefined';

    const client = isBrowser ? getApolloClient() : createApolloClient();

    if ('getInitialProps' in Page) {
        // .getInitialProps() is a special case whereby we must recreate and
        // extend a function prototype with the same name and signature
        const WithApollo = (props: P) => {
            return (
                <ApolloProvider client={client}>
                    <Page {...props} />
                </ApolloProvider>
            );
        };
        WithApollo.displayName = `WithApollo(${getDisplayName(Page)})`;
        WithApollo.getInitialProps = Page.getInitialProps;
        return WithApollo;
    }

    return (props: P) => (
        <ApolloProvider client={client}>
            <Page {...props} />
        </ApolloProvider>
    );
}
